import {
  SvgIcon,
  SvgIconProps,
  SxProps,
  Theme,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { DirectionsCar, EvStation, LocalParking } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../app/helpers";
import { RootState } from "../../../../app/rootReducer";
import { ReactComponent as AvatarIcon } from "../../../../features/Booking-Form/assets/avatar.svg";
import { ReactComponent as ConferenceIcon } from "../../../../features/Booking-Form/assets/conference.svg";
import { ResourceBookingIcon } from "../../../../features/Booking-Form/components/ResourceBookingIcon";
import { BookingType } from "../../../../features/Booking-Form/typings/booking-inputs";
import {
  CONFERENCE_ZONE_TYPE_ID,
  ELECTRIC_CHARGING_PLACE_TYPE_ID,
  PARKING_PLACE_TYPE_ID,
  VEHICLE_PLACE_TYPE_ID,
  WORKPLACE_PLACE_TYPE_ID
} from "../../../../features/Booking-Form/typings/place-and-zone-type-ids.constant";

type P = {
  buttonStyle?: SxProps<Theme>;
  iconSize?: string;
  typToggle: BookingType | null;
  setTypToggle: (t: BookingType) => void;
  onEnableFetch: (t: BookingType) => void;
  /** Optional whitelist for contexts that should show only specific booking types. */
  allowedBookingTypes?: BookingType[];
  /** Extra right margin for report filter rows; disable when parent Stack already gaps items. */
  withTrailingMargin?: boolean;
  /** Associates the group with an external visible label (e.g. toolbar field). */
  ariaLabelledBy?: string;
};
const BookingTypeSelector: React.FC<P> = ({
  buttonStyle,
  iconSize,
  typToggle,
  setTypToggle,
  onEnableFetch,
  allowedBookingTypes,
  withTrailingMargin = true,
  ariaLabelledBy
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isBrowser = useMediaQuery(theme.breakpoints.up(768));

  const { placeTypes, zoneTypes } = useSelector((state: RootState) => state.login.userInformation);
  const { workplaceBookingEnabled } = useSelector(
    (state: RootState) => state.login.userInformation.company.meta
  );

  const hasPlaceTypeId = (id: number) => placeTypes?.some(pt => pt.id === id) ?? false;
  const hasConferenceZoneType = zoneTypes?.some(zt => zt.id === CONFERENCE_ZONE_TYPE_ID) ?? false;
  const isAllowed = (type: BookingType) =>
    !allowedBookingTypes || allowedBookingTypes.includes(type);

  const iconSizePx = iconSize ?? (isBrowser ? "30px" : "20px");

  const handleBookingTypeToggle = (
    event: React.MouseEvent<HTMLElement>,
    newToggles: BookingType | null
  ) => {
    // enforce that at least one button must be active
    /* istanbul ignore next */
    if (newToggles !== null) {
      setTypToggle(newToggles);
      onEnableFetch(newToggles);
    }
  };

  return (
    <ToggleButtonGroup
      value={typToggle}
      exclusive
      onChange={handleBookingTypeToggle}
      data-testid="bookingtype-toggle-btn-group"
      aria-labelledby={ariaLabelledBy}
      sx={[
        { display: "flex", flexWrap: "nowrap" },
        isBrowser && withTrailingMargin ? { marginRight: 3 } : { marginRight: 0 }
      ]}
    >
      {hasPlaceTypeId(WORKPLACE_PLACE_TYPE_ID) &&
        workplaceBookingEnabled &&
        isAllowed(BookingType.WORKPLACE) && (
          <ToggleButton
            value={BookingType.WORKPLACE}
            data-testid={`${BookingType.WORKPLACE}-toggle-btn`}
            aria-label={t("Workplace")}
            sx={buttonStyle ? { ...buttonStyle } : {}}
          >
            <StyledIconComponent iconSize={iconSizePx}>
              <AvatarIcon width={iconSizePx} height={iconSizePx} />
            </StyledIconComponent>
          </ToggleButton>
        )}

      {hasPlaceTypeId(PARKING_PLACE_TYPE_ID) && isAllowed(BookingType.PARKINGPLACE) && (
        <ToggleButton
          value={BookingType.PARKINGPLACE}
          data-testid={`${BookingType.PARKINGPLACE}-toggle-btn`}
          aria-label={t("Parking")}
        >
          <StyledIconComponent iconSize={iconSizePx}>
            <LocalParking width={iconSizePx} height={iconSizePx} />
          </StyledIconComponent>
        </ToggleButton>
      )}

      {hasPlaceTypeId(ELECTRIC_CHARGING_PLACE_TYPE_ID) &&
        isAllowed(BookingType.ELECTRICCHARGINGSTATIONPLACE) && (
          <ToggleButton
            value={BookingType.ELECTRICCHARGINGSTATIONPLACE}
            data-testid={`${BookingType.ELECTRICCHARGINGSTATIONPLACE}-toggle-btn`}
            aria-label={t("Electric Charging")}
          >
            <StyledIconComponent iconSize={iconSizePx}>
              <EvStation width={iconSizePx} height={iconSizePx} />
            </StyledIconComponent>
          </ToggleButton>
        )}

      {hasConferenceZoneType && isAllowed(BookingType.CONFERENCEZONE) && (
        <ToggleButton
          value={BookingType.CONFERENCEZONE}
          data-testid={`${BookingType.CONFERENCEZONE}-toggle-btn`}
          aria-label={t("Conference")}
        >
          <StyledIconComponent iconSize={iconSizePx}>
            <ConferenceIcon width={iconSizePx} height={iconSizePx} />
          </StyledIconComponent>
        </ToggleButton>
      )}
      {allowedBookingTypes?.includes(BookingType.VEHICLE) &&
        hasPlaceTypeId(VEHICLE_PLACE_TYPE_ID) && (
          <ToggleButton
            value={BookingType.VEHICLE}
            data-testid={`${BookingType.VEHICLE}-toggle-btn`}
            aria-label={t("Vehicle")}
          >
            <StyledIconComponent iconSize={iconSizePx}>
              <DirectionsCar width={iconSizePx} height={iconSizePx} />
            </StyledIconComponent>
          </ToggleButton>
        )}

      {allowedBookingTypes?.includes(BookingType.RESOURCE) && (
        <ToggleButton
          value={BookingType.RESOURCE}
          data-testid={`${BookingType.RESOURCE}-toggle-btn`}
          sx={buttonStyle ? { padding: "6px", ...buttonStyle } : { padding: "6px" }}
        >
          <ResourceBookingIcon />
        </ToggleButton>
      )}
    </ToggleButtonGroup>
  );
};

export default BookingTypeSelector;

interface StyledIconProps extends SvgIconProps {
  iconSize?: string;
}

export function StyledIconComponent({ iconSize = "120px", ...rest }: Readonly<StyledIconProps>) {
  const theme = useTheme();

  return (
    <SvgIcon
      {...rest}
      viewBox={`0 0 ${iconSize.slice(0, -2)} ${iconSize.slice(0, -2)}`}
      sx={{
        width: iconSize,
        height: iconSize,
        "& path": { fill: theme.palette.text.primary }
      }}
    />
  );
}
